/**
 * useSegmentFillAnimation — the thin DOM/React glue over the pure animated-fill
 * policy (WL-058, deck `concepts/segment-fills` round 4).
 *
 * ALL decisions live in `logic/segmentFillAnimation.js` (pure, unit-tested);
 * this hook only supplies the three runtime signals that policy needs and can't
 * be pure about, then executes its verdict:
 *
 *   - reduced-motion  ← `matchMedia("(prefers-reduced-motion: reduce)")`
 *   - onScreen        ← `IntersectionObserver` on the fill element (offscreen ⇒
 *                        PAUSE, not stop — the guardrail from DESIGN-DECISIONS
 *                        row 7)
 *   - the pan itself  ← `element.animate(keyframes, timing)` (Web-Animations;
 *                        `transform`-only ⇒ GPU-composited, never a repaint)
 *
 * Contract when WAA is unavailable, the viewer prefers reduced motion, the
 * Segment is offscreen/closed, or the fill is a poster/non-GIF: the hook creates
 * NO running animation and returns the static poster source. These guardrails are
 * driven by the REAL signals (matchMedia + IntersectionObserver + the pure
 * policy), not a feature flag — the animation is unconditional design now.
 *
 * Usage:
 *   const { ref, src, showChip, running } = useSegmentFillAnimation({
 *     fill, open,
 *   });
 *   // attach ref to the element whose CSS background is `url(src)`.
 *
 * @param {{
 *   fill:object, open?:boolean,
 *   panFraction?:number, durationMs?:number
 * }} args
 * @returns {{ ref:Function, src:string, showChip:boolean, running:boolean,
 *             animate:boolean }}
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { resolveFillPlayback } from "./logic/segmentFillAnimation";

const prefersReducedMotion = () => {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch (e) {
    return false;
  }
};

const canAnimate = (el) =>
  !!el && typeof el.animate === "function" && typeof IntersectionObserver !== "undefined";

export default function useSegmentFillAnimation({
  fill,
  open = false,
  panFraction,
  durationMs,
} = {}) {
  const [reducedMotion, setReducedMotion] = useState(prefersReducedMotion);
  const [onScreen, setOnScreen] = useState(true);

  const elRef = useRef(null);
  const animRef = useRef(null); // the live Web-Animations Animation handle
  const observerRef = useRef(null);

  // --- reduced-motion, live ---
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const on = () => setReducedMotion(mq.matches);
    on();
    mq.addEventListener ? mq.addEventListener("change", on) : mq.addListener(on);
    return () =>
      mq.removeEventListener
        ? mq.removeEventListener("change", on)
        : mq.removeListener(on);
  }, []);

  // Policy verdict (pure). offscreen ⇒ paused (poster held).
  const playback = resolveFillPlayback(fill, {
    open,
    reducedMotion,
    onScreen,
    panFraction,
    durationMs,
  });

  // Ref callback: (dis)connect the IntersectionObserver as the node mounts.
  const ref = useCallback(
    (node) => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      elRef.current = node;
      if (!node || typeof IntersectionObserver === "undefined") return;
      const io = new IntersectionObserver(
        (entries) => {
          const e = entries[0];
          if (e) setOnScreen(e.isIntersecting && e.intersectionRatio > 0);
        },
        { threshold: [0, 0.01] }
      );
      io.observe(node);
      observerRef.current = io;
    },
    []
  );

  // Drive the Web-Animations pan from the policy verdict.
  const shouldRun = playback.running && !!playback.keyframes;
  useEffect(() => {
    const el = elRef.current;
    if (!canAnimate(el) || !playback.keyframes) {
      // Tear down any existing animation → element rests on its poster.
      if (animRef.current) {
        animRef.current.cancel();
        animRef.current = null;
      }
      return undefined;
    }

    if (!animRef.current) {
      animRef.current = el.animate(playback.keyframes, playback.timing);
    }
    const anim = animRef.current;
    try {
      if (shouldRun) anim.play();
      else anim.pause(); // offscreen-pause: keep the handle, hold the frame
    } catch (e) {
      /* WAA can throw if the element detached mid-frame; ignore */
    }

    return () => {
      // Cancel only on full unmount / dependency change away from animatable.
      if (animRef.current && !playback.keyframes) {
        animRef.current.cancel();
        animRef.current = null;
      }
    };
    // Re-run when the run decision or the fill source changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRun, playback.src]);

  // Final unmount cleanup.
  useEffect(
    () => () => {
      if (animRef.current) {
        animRef.current.cancel();
        animRef.current = null;
      }
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    },
    []
  );

  return {
    ref,
    src: playback.src,
    showChip: playback.showChip,
    running: shouldRun,
    animate: playback.animate,
  };
}
